// ============================================
// STATE DEFINITIONS - Single source of truth
// ============================================

/** States that support part-based animation (antennae, wings, body, etc.) */
export const PART_ANIMATED_STATES = ['curious', 'thinking', 'disappointed', 'excited', 'searching', 'buzzing'] as const;

/** State that uses crossfade (too different for part animation) */
export const CROSSFADE_STATES = ['bored'] as const;

/** All Bizy states */
export const ALL_BIZY_STATES = [...PART_ANIMATED_STATES, ...CROSSFADE_STATES] as const;

/** Type for part-animated states */
export type PartAnimatedState = (typeof PART_ANIMATED_STATES)[number];

/** Type for crossfade states */
export type CrossfadeState = (typeof CROSSFADE_STATES)[number];

/** Type for all Bizy states */
export type BizyState = (typeof ALL_BIZY_STATES)[number];

/** Type guard for part-animated states */
export const isPartAnimatedState = (state: BizyState): state is PartAnimatedState => {
	return (PART_ANIMATED_STATES as readonly string[]).includes(state);
};

// ============================================
// PART STATE TYPE - Structure for animated state data
// ============================================

export type PartState = {
	antenna1: { path: string };
	antenna2: { path: string };
	body: { x: number; y: number; width: number; height: number; rx: number };
	bodyHighlight: { x: number; y: number; width: number; height: number; rx: number };
	stripes: { y1: number; y2: number; y3: number; height: number };
	eyes: { leftPath: string; rightPath: string };
	wing1Path: string;
	wing2Path: string;
	wing3Path: string;
	wing4Path: string;
	arms: { d: string; side: 'left' | 'right' }[];
	stinger: string;
	stars?: string[];
	badge: {
		outerEllipse: { cx: number; cy: number; rx: number; ry: number };
		innerEllipse: { cx: number; cy: number; rx: number; ry: number };
		starPath: string;
		starHighlightPath: string;
	};
};

// ============================================
// COMPONENT PROPS
// ============================================

export type BizySize = 'sm' | 'md' | 'lg' | number;

export type BizyStateProps = {
	/** Width of the SVG */
	width?: number | string;
	/** Height of the SVG */
	height?: number | string;
	/** Optional className */
	className?: string;
};

export type BizyProps = {
	/** The emotional state of Bizy */
	state?: BizyState;
	/** Size of the component - 'sm' (48px), 'md' (80px), 'lg' (120px), or custom number */
	size?: BizySize;
	/** Optional className for additional styling */
	className?: string;
};
